import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import { DOCUMENT_TYPES, UNKNOWN_DOCUMENT_TYPE, getDocumentTypeConfig } from '../lib/documentTypes'

// NOTE: entity_type classification now lives in a single shared source (lib/documentTypes.js).
// This used to be a local ENTITY_CONFIG object here that was missing 'equipment_contract'
// and 'equipment_ipc' — those documents were silently falling into "Other". See project
// architecture notes (entity_type unification sprint) for details.

const fmtSize = (b) => {
  if (!b) return ''
  if (b < 1024) return b + ' B'
  if (b < 1024*1024) return (b/1024).toFixed(1) + ' KB'
  return (b/1024/1024).toFixed(1) + ' MB'
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'

const getFileIcon = (name) => {
  const ext = (name || '').split('.').pop().toLowerCase()
  if (['pdf'].includes(ext)) return '📄'
  if (['xls','xlsx','csv'].includes(ext)) return '📊'
  if (['doc','docx'].includes(ext)) return '📝'
  if (['jpg','jpeg','png','gif','webp'].includes(ext)) return '🖼'
  if (['zip','rar','7z'].includes(ext)) return '🗜'
  return '📎'
}

export default function DocumentCenter({ initProject }) {
  const [selectedProject, setSelectedProject] = useState(null)
  const [projects, setProjects]               = useState([])
  // Real authorization source -- Documents.Delete is the only write
  // permission this page actually needs (confirmed: no Create/Edit
  // path exists in this component, only upload -- which is unaffected
  // by this batch's scope -- and delete).
  const { can } = useProjectAuth(selectedProject?.id)
  const [docs, setDocs]                       = useState([])
  const [loading, setLoading]                 = useState(false)
  const [search, setSearch]                   = useState('')
  const [activeType, setActiveType]           = useState('all')
  const [sortBy, setSortBy]                   = useState('date') // date | name | size | type
  const [viewMode, setViewMode]               = useState('list') // list | grid

  useEffect(() => {
    supabase.from('projects').select('id, name').order('name').then(({ data }) => setProjects(data || []))
  }, [])

  useEffect(() => {
    if (initProject) setSelectedProject(initProject)
  }, [initProject])

  useEffect(() => {
    if (selectedProject?.id) loadDocs(selectedProject.id)
  }, [selectedProject?.id]) // eslint-disable-line

  const loadDocs = useCallback(async (projectId) => {
    setLoading(true)
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('project_id', projectId)
      .order('uploaded_at', { ascending: false })
    if (error) console.error('docs error:', error.message)
    setDocs(data || [])
    setLoading(false)
  }, [])

  const openDoc = async (doc) => {
    const { data } = await supabase.storage.from('documents').createSignedUrl(doc.file_path, 3600)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
  }

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.file_name}"?`)) return
    await supabase.storage.from('documents').remove([doc.file_path])
    await supabase.from('documents').delete().eq('id', doc.id)
    setDocs(prev => prev.filter(d => d.id !== doc.id))
  }

  // Filter + sort
  const filtered = docs
    .filter(d => activeType === 'all' || d.entity_type === activeType)
    .filter(d => !search || d.file_name?.toLowerCase().includes(search.toLowerCase()) || d.entity_type?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'date') return new Date(b.uploaded_at) - new Date(a.uploaded_at)
      if (sortBy === 'name') return (a.file_name || '').localeCompare(b.file_name || '')
      if (sortBy === 'size') return (b.file_size || 0) - (a.file_size || 0)
      if (sortBy === 'type') return (a.entity_type || '').localeCompare(b.entity_type || '')
      return 0
    })

  // Stats by type
  const stats = {}
  docs.forEach(d => {
    const t = d.entity_type || 'other'
    if (!stats[t]) stats[t] = { count: 0, size: 0 }
    stats[t].count++
    stats[t].size += d.file_size || 0
  })
  const totalSize = docs.reduce((s, d) => s + (d.file_size || 0), 0)

  if (!selectedProject) return (
    <div style={{ padding:'32px 24px', maxWidth:700 }}>
      <h1 style={{ fontSize:22, fontWeight:800, color:'#0F1017', marginBottom:6 }}>Document Center</h1>
      <p style={{ fontSize:13, color:'#6B7280', marginBottom:24 }}>مركز الوثائق — كل الملفات المرفوعة في مكان واحد</p>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        {projects.map(p => (
          <button key={p.id} onClick={() => setSelectedProject(p)}
            style={{ textAlign:'left', background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:16, cursor:'pointer' }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#0F1017' }}>{p.name}</div>
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', fontFamily:"-apple-system,'Helvetica Neue',sans-serif" }}>

      {/* Header */}
      <div style={{ padding:'18px 24px 14px', background:'#fff', borderBottom:'1px solid #E5E7EB', flexShrink:0 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:'#0F1017' }}>Document Center</div>
            <div style={{ fontSize:11.5, color:'#6B7280', marginTop:3 }}>
              {selectedProject.name} · {docs.length} files · {fmtSize(totalSize)}
              <button onClick={() => setSelectedProject(null)} style={{ marginLeft:8, fontSize:10, color:'#9CA3AF', border:'1px solid #E5E7EB', borderRadius:4, padding:'1px 6px', cursor:'pointer', background:'none' }}>× Change</button>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div style={{ display:'flex', gap:8, marginTop:14, flexWrap:'wrap' }}>
          <button onClick={() => setActiveType('all')}
            style={{ padding:'5px 12px', borderRadius:20, fontSize:11.5, fontWeight:600, cursor:'pointer', border:'none',
              background: activeType==='all' ? '#0F1017' : '#F3F4F6',
              color: activeType==='all' ? '#fff' : '#6B7280' }}>
            All ({docs.length})
          </button>
          {Object.entries(stats).map(([type, s]) => {
            const cfg = getDocumentTypeConfig(type)
            return (
              <button key={type} onClick={() => setActiveType(type)}
                style={{ padding:'5px 12px', borderRadius:20, fontSize:11.5, fontWeight:600, cursor:'pointer',
                  border: activeType===type ? 'none' : '1px solid #E5E7EB',
                  background: activeType===type ? cfg.color : '#fff',
                  color: activeType===type ? '#fff' : '#374151' }}>
                {cfg.icon} {cfg.label} ({s.count})
              </button>
            )
          })}
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 24px', background:'#fff', borderBottom:'1px solid #E5E7EB', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', background:'#F3F4F6', borderRadius:8, flex:1, maxWidth:300 }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="#9CA3AF" strokeWidth="1.4"><circle cx="6.5" cy="6.5" r="4.5"/><line x1="9.5" y1="9.5" x2="14" y2="14"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search files…"
            style={{ border:'none', background:'none', fontSize:12, outline:'none', width:'100%' }}/>
          {search && <button onClick={() => setSearch('')} style={{ border:'none', background:'none', color:'#9CA3AF', cursor:'pointer', fontSize:11 }}>✕</button>}
        </div>
        <div style={{ flex:1 }}/>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          style={{ padding:'5px 10px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:12, background:'#fff', cursor:'pointer' }}>
          <option value="date">Sort: Date</option>
          <option value="name">Sort: Name</option>
          <option value="size">Sort: Size</option>
          <option value="type">Sort: Type</option>
        </select>
        <div style={{ display:'flex', border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden' }}>
          {['list','grid'].map(m => (
            <button key={m} onClick={() => setViewMode(m)}
              style={{ padding:'5px 10px', fontSize:12, border:'none', cursor:'pointer',
                background: viewMode===m ? '#0F1017' : '#fff',
                color: viewMode===m ? '#fff' : '#6B7280' }}>
              {m === 'list' ? '☰' : '⊞'}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex:1, overflow:'auto', padding:'16px 24px' }}>
        {loading ? (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:200, color:'#9CA3AF' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 0', color:'#9CA3AF' }}>
            <div style={{ fontSize:32, marginBottom:8 }}>📁</div>
            <div style={{ fontSize:14 }}>No documents found</div>
          </div>
        ) : viewMode === 'list' ? (
          <div style={{ background:'#fff', borderRadius:12, border:'1px solid #E5E7EB', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
              <thead>
                <tr style={{ background:'#F9FAFB' }}>
                  {['File','Type','Size','Date',''].map(h => (
                    <th key={h} style={{ padding:'9px 14px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', borderBottom:'1px solid #E5E7EB' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((doc, i) => {
                  const cfg = getDocumentTypeConfig(doc.entity_type)
                  return (
                    <tr key={doc.id} style={{ background: i%2===0?'#fff':'#FAFAFA', borderBottom:'1px solid #F3F4F6' }}>
                      <td style={{ padding:'10px 14px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ fontSize:18 }}>{getFileIcon(doc.file_name)}</span>
                          <div>
                            <div style={{ fontWeight:600, color:'#0F1017' }}>{doc.file_name}</div>
                            {doc.entity_ref && <div style={{ fontSize:11, color:'#9CA3AF', marginTop:1 }}>{doc.entity_ref}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding:'10px 14px' }}>
                        <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:12, background:cfg.bg, color:cfg.color }}>
                          {cfg.icon} {cfg.label}
                        </span>
                      </td>
                      <td style={{ padding:'10px 14px', color:'#6B7280', fontVariantNumeric:'tabular-nums' }}>{fmtSize(doc.file_size)}</td>
                      <td style={{ padding:'10px 14px', color:'#6B7280' }}>{fmtDate(doc.uploaded_at)}</td>
                      <td style={{ padding:'10px 14px' }}>
                        <div style={{ display:'flex', gap:4 }}>
                          <button onClick={() => openDoc(doc)}
                            style={{ padding:'3px 10px', fontSize:11, fontWeight:600, border:'1px solid #E5E7EB', borderRadius:6, background:'#fff', color:'#374151', cursor:'pointer' }}>
                            Open
                          </button>
                          {can('Documents.Delete') && (
                            <button onClick={() => deleteDoc(doc)}
                              style={{ padding:'3px 8px', fontSize:11, border:'1px solid #FEE2E2', borderRadius:6, background:'#fff', color:'#DC2626', cursor:'pointer' }}>
                              🗑
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* Grid view */
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:12 }}>
            {filtered.map(doc => {
              const cfg = getDocumentTypeConfig(doc.entity_type)
              return (
                <div key={doc.id} style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, padding:14, cursor:'pointer', transition:'box-shadow .15s' }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,.08)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
                  <div style={{ fontSize:32, marginBottom:8, textAlign:'center' }}>{getFileIcon(doc.file_name)}</div>
                  <div style={{ fontSize:12, fontWeight:600, color:'#0F1017', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:4 }}>{doc.file_name}</div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                    <span style={{ fontSize:10, fontWeight:600, padding:'2px 6px', borderRadius:10, background:cfg.bg, color:cfg.color }}>{cfg.label}</span>
                    <span style={{ fontSize:10, color:'#9CA3AF' }}>{fmtSize(doc.file_size)}</span>
                  </div>
                  <div style={{ fontSize:10, color:'#9CA3AF', marginBottom:10 }}>{fmtDate(doc.uploaded_at)}</div>
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={() => openDoc(doc)}
                      style={{ flex:1, padding:'5px 0', fontSize:11, fontWeight:600, border:'1px solid #E5E7EB', borderRadius:6, background:'#fff', color:'#374151', cursor:'pointer' }}>
                      Open
                    </button>
                    {can('Documents.Delete') && (
                      <button onClick={() => deleteDoc(doc)}
                        style={{ padding:'5px 8px', fontSize:11, border:'1px solid #FEE2E2', borderRadius:6, background:'#fff', color:'#DC2626', cursor:'pointer' }}>
                        🗑
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
